import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

const RULES = [
 { rule: 'Default', call: 'show()', binds: 'undefined (strict) / global object' },
 { rule: 'Implicit', call: 'user.show()', binds: 'the object before the dot (user)' },
 { rule: 'Explicit', call: 'show.call(user)', binds: 'whatever you pass (user)' },
 { rule: 'new', call: 'new User()', binds: 'the brand-new object' },
];

export const JSThisKeyword: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">The this Keyword</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    <code>this</code> isn&apos;t decided when a function is written — it&apos;s decided by <em>how</em> the function is called. Master that one idea and almost every <code>this</code> bug makes sense.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="18 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>An order object with a method that prints its total:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const order = {\n  id: "ORD-101",\n  totalAmount: 1500,\n  print() {\n    console.log(this.totalAmount);\n  },\n};\n\norder.print();          // 1500\n\nconst printOrder = order.print;\nprintOrder();           // undefined (or the global object)` }]} defaultLanguage="javascript" id="this-problem" />
   <Callout tone="info" title="The puzzle">
    The <em>same</em> function produced two different results. Why? Because <Code>this</Code> depends on how the function is <Mark>called</Mark>, not where it&apos;s defined.
   </Callout>
  </section>

  {/* 2. Why Existing Solutions Fail */}
  <section className="mb-12">
   <SectionHeading n={2}>Why this Exists</SectionHeading>
   <Para>Without <Code>this</Code>, every function that operates on an object would have to receive that object explicitly:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function printUser(user) { console.log(user.name); }\nfunction printOrder(order) { console.log(order.amount); }\n// you keep passing the object manually, everywhere` }]} defaultLanguage="javascript" id="this-without" />
   <Para><Code>this</Code> lets a function ask <Mark>&quot;who called me?&quot;</Mark> without the caller passing itself in — so the same method can serve many objects.</Para>
  </section>

  {/* 3. Definition */}
  <section className="mb-12">
   <SectionHeading n={3}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Code>this</Code> is a special keyword that refers to the object currently executing the function. It is <Mark>not</Mark> decided when the function is created — it&apos;s decided when the function is <Mark>called</Mark>.</p>
   </div>
   <Callout tone="warning" title="The biggest interview myth">
    <Code>this</Code> does <em>not</em> belong to the function. It depends on <Mark>how</Mark> the function is called, not <Mark>where</Mark> it&apos;s written.
   </Callout>
  </section>

  {/* 4. Core Idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea: Who Called It?</SectionHeading>
   <Para>For any <Code>this</Code>, ask one question: <Mark>&quot;Who called the function?&quot;</Mark> — not &quot;where was it defined?&quot; That single rule resolves most <Code>this</Code> puzzles.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `user.greet();  // called by user      → this = user\ngreet();       // called by nobody    → this = undefined/global\ngreet.call(x); // called with x       → this = x\nnew Greet();   // called with new     → this = new object` }]} defaultLanguage="javascript" id="this-core" />
  </section>

  {/* 5. Internal Working: the four rules */}
  <section className="mb-12">
   <SectionHeading n={5}>Internal Working: The Four Binding Rules</SectionHeading>
   <Para>Every <Code>this</Code> value comes from one of four rules, checked in order of precedence (<Code>new</Code> &gt; explicit &gt; implicit &gt; default):</Para>
   <div className="my-4 overflow-x-auto">
    <table className="w-full text-left text-[13px] border border-gray-200 dark:border-gray-700 rounded-lg">
     <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
      <tr>
       <th className="px-3 py-2 font-semibold">Rule</th>
       <th className="px-3 py-2 font-semibold">Call</th>
       <th className="px-3 py-2 font-semibold">this binds to</th>
      </tr>
     </thead>
     <tbody className="text-gray-700 dark:text-gray-300">
      {RULES.map((r) => (
       <tr key={r.rule} className="border-t border-gray-200 dark:border-gray-700">
        <td className="px-3 py-2 font-semibold text-gray-800 dark:text-gray-200">{r.rule}</td>
        <td className="px-3 py-2 font-mono text-amber-700 dark:text-amber-300">{r.call}</td>
        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.binds}</td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
  </section>

  {/* 6. Visual Flow: global, default, implicit */}
  <section className="mb-12">
   <SectionHeading n={6}>Visual Flow: Default &amp; Implicit Binding</SectionHeading>
   <SubHeading>Standalone call → default binding</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `"use strict";\nfunction show() {\n  console.log(this);\n}\nshow(); // undefined  (without strict mode in a browser: the window object)` }]} defaultLanguage="javascript" id="this-default" />
   <SubHeading>Called via an object → implicit binding</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  name: "Sachin",\n  greet() { console.log(this.name); },\n};\nuser.greet(); // "Sachin"  — user is before the dot, so this = user` }]} defaultLanguage="javascript" id="this-implicit" />
   <CodeBlock snippets={[{ language: 'text', code: `user.greet()\n  ↑ caller\n  → this = user` }]} defaultLanguage="text" id="this-implicit-flow" />
  </section>

  {/* 7. Simple Example: method extraction */}
  <section className="mb-12">
   <SectionHeading n={7}>Simple Example: Why Method Extraction Breaks</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  name: "Sachin",\n  greet() { console.log(this.name); },\n};\n\nconst fn = user.greet; // pulled off the object\nfn();                  // undefined` }]} defaultLanguage="javascript" id="this-extract" />
   <Para>Once you detach the function, <Code>user</Code> no longer calls it — <Code>fn()</Code> does. With no object before the dot, default binding kicks in and <Code>this.name</Code> is gone. This is one of the most common JS bugs.</Para>
  </section>

  {/* 8. Deep Example: call / apply / bind */}
  <section className="mb-12">
   <SectionHeading n={8}>Deep Example: call, apply &amp; bind</SectionHeading>
   <Para>Explicit binding lets you set <Code>this</Code> yourself:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function greet(city) {\n  console.log(this.name, city);\n}\nconst user = { name: "Sachin" };\n\ngreet.call(user, "Bangalore");   // Sachin Bangalore — args listed\ngreet.apply(user, ["Bangalore"]); // Sachin Bangalore — args as array\n\nconst bound = greet.bind(user);  // returns a NEW function, runs later\nbound("Bangalore");              // Sachin Bangalore` }]} defaultLanguage="javascript" id="this-cab" />
   <TwoUp
    left={{ title: 'Execute now', items: ['call(thisArg, ...args)', 'apply(thisArg, [args])'] }}
    right={{ title: 'Execute later', items: ['bind(thisArg) → new fn', 'this is permanently fixed'] }}
   />
   <Para>Remember the earlier extraction bug? <Code>bind</Code> fixes it: <Code>const fn = user.greet.bind(user)</Code> locks <Code>this</Code> to <Code>user</Code> forever.</Para>
  </section>

  {/* 9. Production Example: constructors + events + React */}
  <section className="mb-12">
   <SectionHeading n={9}>Production Example</SectionHeading>
   <SubHeading>Constructor functions / new</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `function User(name) {\n  this.name = name; // 'this' is the brand-new object\n}\nconst user = new User("Sachin");\nconsole.log(user); // { name: "Sachin" }` }]} defaultLanguage="javascript" id="this-new" />
   <Para><Code>new</Code> creates an empty object, binds <Code>this</Code> to it, runs the constructor, and returns it.</Para>
   <SubHeading>Event handlers</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `button.addEventListener("click", function () {\n  console.log(this); // the button element — the DOM called it\n});\n\nbutton.addEventListener("click", () => {\n  console.log(this); // the OUTER scope's this (lexical)\n});` }]} defaultLanguage="javascript" id="this-event" />
   <SubHeading>The React class-component pattern</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  constructor() {\n    this.handleClick = this.handleClick.bind(this); // restore context\n  }\n}` }]} defaultLanguage="javascript" id="this-react" />
   <Para>Older React leaned on <Code>bind(this)</Code> everywhere because passing a method as a callback loses its context.</Para>
  </section>

  {/* 10. Engineering Tradeoffs: arrow vs normal */}
  <section className="mb-12">
   <SectionHeading n={10}>Engineering Tradeoffs: Arrow vs Normal Functions</SectionHeading>
   <Para>Arrow functions do <Mark>not</Mark> create their own <Code>this</Code> — they inherit it from the surrounding (lexical) scope. That&apos;s a feature in callbacks and a trap as object methods:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  name: "Sachin",\n  greet: () => {\n    console.log(this.name); // undefined — 'this' is the outer scope, not user\n  },\n};` }]} defaultLanguage="javascript" id="this-arrow-trap" />
   <TwoUp
    left={{ title: 'Normal function', items: ['Creates its own this', 'Decided by how it&apos;s called', 'Good for object methods'] }}
    right={{ title: 'Arrow function', items: ['No own this (lexical)', 'Inherits surrounding this', 'Good for callbacks'] }}
   />
  </section>

  {/* 11. Performance Considerations -> the callback win */}
  <section className="mb-12">
   <SectionHeading n={11}>Where Arrow this Shines: Callbacks</SectionHeading>
   <Para>The classic callback problem — a normal function inside <Code>setTimeout</Code> gets its own <Code>this</Code> and loses the object:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  name: "Sachin",\n  greet() {\n    setTimeout(function () {\n      console.log(this.name); // undefined — callback has its own this\n    }, 1000);\n  },\n};` }]} defaultLanguage="javascript" id="this-cb-bug" />
   <Para>An arrow function fixes it by inheriting <Code>this</Code> from <Code>greet</Code> (which is <Code>user</Code>):</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  name: "Sachin",\n  greet() {\n    setTimeout(() => {\n      console.log(this.name); // "Sachin"\n    }, 1000);\n  },\n};` }]} defaultLanguage="javascript" id="this-cb-fix" />
  </section>

  {/* 12. Common Bugs */}
  <section className="mb-12">
   <SectionHeading n={12}>Common Bugs</SectionHeading>
   <Callout tone="warning" title="Thinking this belongs to the function">It&apos;s set at call time, not definition time.</Callout>
   <Callout tone="warning" title="Method extraction">Storing <Code>const fn = obj.method</Code> drops the binding — use <Code>bind</Code>.</Callout>
   <Callout tone="warning" title="Arrow as an object method">An arrow method&apos;s <Code>this</Code> is the outer scope, not the object.</Callout>
   <Callout tone="warning" title="Normal callback in setTimeout/Promise">The callback gets its own <Code>this</Code> — use an arrow or <Code>bind</Code>.</Callout>
   <Callout tone="warning" title="Forgetting new">Calling a constructor without <Code>new</Code> binds <Code>this</Code> to global/undefined.</Callout>
  </section>

  {/* 13. Debugging Stories */}
  <section className="mb-12">
   <SectionHeading n={13}>Debugging Stories</SectionHeading>
   <Callout tone="info" title="The handler that lost its object">
    A team passed <Code>onClick={'{this.save}'}</Code> in a React class component and got &quot;cannot read property of undefined.&quot; The method was detached from the instance, so <Code>this</Code> was undefined. Binding it in the constructor (or using a class field arrow) restored the instance.
   </Callout>
   <Callout tone="info" title="The arrow method that saw nothing">
    Someone wrote an object method as an arrow function for &quot;modern style&quot; and <Code>this.name</Code> came back undefined. Arrows have no own <Code>this</Code>; switching it to a normal method shorthand fixed it instantly.
   </Callout>
  </section>

  {/* 14. Interview Discussion */}
  <section className="mb-12">
   <SectionHeading n={14}>Interview Discussion</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['What is this?', 'How is this determined?', 'Arrow vs normal function?', 'call vs apply vs bind?', 'Why does method extraction break?', 'Why does setTimeout lose this?', 'What is lexical this?', 'How does new affect this?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="How is the value of this determined?">
    By how the function is called, resolved through four rules: <Code>new</Code> binding, explicit (<Code>call/apply/bind</Code>), implicit (<Code>obj.method()</Code>), then default (global/undefined). Arrow functions ignore all four and inherit <Code>this</Code> lexically.
   </QA>
   <QA q="What is the difference between call, apply, and bind?">
    <Code>call</Code> and <Code>apply</Code> invoke immediately with an explicit <Code>this</Code> — <Code>call</Code> takes comma-separated args, <Code>apply</Code> takes an array. <Code>bind</Code> doesn&apos;t invoke; it returns a new function with <Code>this</Code> permanently fixed.
   </QA>
   <QA q="Why does setTimeout lose this?">
    A normal function callback is invoked by the timer with no object before a dot, so default binding applies and <Code>this</Code> is global/undefined. An arrow function inherits <Code>this</Code> from the enclosing method, preserving it.
   </QA>
   <QA q="What is lexical this?">
    Arrow functions don&apos;t create their own <Code>this</Code>; they capture it from the scope where they were defined. That captured value is &quot;lexical this&quot; — fixed by location, not by caller.
   </QA>
  </section>

  {/* 15. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={15}>Mental Model</SectionHeading>
   <Para>Whenever you see <Code>this</Code>, ask <Mark>&quot;who called this function?&quot;</Mark> — never &quot;where was it written?&quot; (except for arrow functions, which always borrow <Code>this</Code> from their surroundings). That single question solves most <Code>this</Code> questions.</Para>
  </section>

  {/* 16. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={16}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     <code>this</code> refers to the object that invokes a function, and its value is determined at call time, not when the function is defined.
    </p>
   </div>
  </section>

  {/* 17. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={17}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    The value of <Code>this</Code> depends on how a function is called. Normal functions create their own <Code>this</Code>; arrow functions inherit it from the surrounding scope. Understanding <Code>call</Code>, <Code>apply</Code>, <Code>bind</Code>, constructors, callbacks, and method invocation explains nearly every <Code>this</Code> interview question and production bug.
   </Callout>
  </section>
 </div>
);

export default JSThisKeyword;
